import type { LocalStorageTokenData } from '../types'
import type { FederatedSession } from './wapi-client'

export interface SessionRestoreDeps {
    /** The raw mirror entry (incl. an expired token), or null when absent/malformed. */
    mirror: LocalStorageTokenData | null
    /** Refresh an expired cognito token (refreshRequest bound to urlRoot). */
    refresh: (opts: {
        email: string
        token: string
        sessionId?: string
    }) => Promise<FederatedSession>
    writeMirror: (entry: LocalStorageTokenData) => void
    setSnowplowUserId: (id: string | null) => void
    setRumUser: (user: { id?: string; email?: string }) => void
}

/**
 * Silently restore a session from the mirror by **refreshing an expired cognito token**
 * (ui-react `cognitoAutoLogIn`/`refreshToken` parity). Only cognito sessions can be
 * refreshed — a federated (Google/Facebook) session re-restores via its SDK, so this
 * returns false for those and lets the chain fall through. On success the refreshed
 * mirror is written and the analytics identity is set. Returns whether a session was
 * (re)established. Throws are surfaced to the chain runner, which keeps the chain fail-safe.
 */
export async function attemptSessionRestore(deps: SessionRestoreDeps): Promise<boolean> {
    const mirror = deps.mirror
    if (mirror?.provider !== 'cognito' || !mirror.email || !mirror.authToken) {
        return false
    }

    const session = await deps.refresh({
        email: mirror.email,
        token: mirror.authToken,
        sessionId: mirror.sessionId || undefined,
    })

    deps.writeMirror({
        authToken: session.token,
        authTokenExp: session.expiresAt,
        email: mirror.email,
        identityId: session.identityId,
        provider: 'cognito',
        sessionId: session.sessionId,
    })
    deps.setSnowplowUserId(session.identityId)
    deps.setRumUser({
        ...(session.identityId ? { id: session.identityId } : {}),
        ...(mirror.email ? { email: mirror.email } : {}),
    })

    return true
}
